import { Box, Stack, Typography } from '@mui/material'
import { ColDef } from 'ag-grid-community'
import { getUserHistory } from 'api/users'
import columnRenderers from 'components/column-renderers'
import updatedByOrgRenderer from 'components/column-renderers/updatedByOrg'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import { useGridControl } from 'hooks/useGridControl'
import { userCellValueRenderer, userFieldNameRenderer } from 'models/user.model'
import { useCallback, useEffect, useMemo } from 'react'
import { BaseTableRequest, EOp } from 'types'
import { useGetDataSource } from 'utils'

export function UserOrgRolesHistory({
  organizationId,
  needUpdate,
  resetNeedUpdate,
}: {
  organizationId: number
  needUpdate: boolean
  resetNeedUpdate: () => void
}) {
  const getUserHistoryCallback = useCallback(
    (params: BaseTableRequest) => {
      const sort = params.sort || { field: 'updatedAt', sort: 'desc' }
      return getUserHistory({
        ...params,
        filter: {
          ...(params.filter || {}),
          fieldName: {
            type: 'text',
            op: EOp.in,
            value: ['type', 'users', 'level 4', 'level 1', 'level 3', 'level 2'],
          },
          organizationId: {
            type: 'number',
            op: EOp.equals,
            value: organizationId,
          },
        },
        sort,
      })
    },
    [organizationId],
  )

  const getDataSource = useGetDataSource(getUserHistoryCallback)

  const { gridRef, onGridReady } = useGridControl(getDataSource)

  const columnDefs: ColDef[] = useMemo(() => {
    const columns: ColDef[] = [
      {
        headerName: 'Action',
        field: 'updateAction',
        maxWidth: 120,
        cellRenderer: columnRenderers.capitalizeRenderer('updateAction'),
      },
      {
        headerName: 'Field',
        field: 'fieldName',
        cellRenderer: userFieldNameRenderer('fieldName'),
        minWidth: 140,
      },
      {
        headerName: 'Old Value',
        field: 'oldValue',
        cellRenderer: userCellValueRenderer('oldValue'),
        minWidth: 300,
      },
      {
        headerName: 'New Value',
        field: 'newValue',
        cellRenderer: userCellValueRenderer('newValue'),
        minWidth: 300,
      },
      { headerName: 'Date', field: 'updatedAt', cellRenderer: columnRenderers.date('updatedAt') },
      {
        headerName: 'Updated by',
        field: 'creator',
        cellRenderer: updatedByOrgRenderer,
        minWidth: 450,
      },
    ]

    return columns
  }, [])

  useEffect(() => {
    if (needUpdate) {
      gridRef.current?.api.purgeInfiniteCache()
      resetNeedUpdate()
    }
  }, [gridRef, needUpdate, resetNeedUpdate])

  return (
    <Stack gap={2} mb={4}>
      <Typography fontWeight={600}>History</Typography>

      <Box height="800px">
        <InfinityAgGrid
          pagination
          innerRef={gridRef}
          columnDefs={columnDefs}
          onGridReady={onGridReady}
        />
      </Box>
    </Stack>
  )
}
